import api from "@api";
import { useToast } from "@UI/hooks/useToast";
import useI18n from "@I18n/hooks/useI18n";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";

import { serviceRequestPathFragment } from "../../config/routes";
import messages from "../messages";
import { useFlowCategoryParams } from "./useFlowPCategory";
import { clearDraftStorage } from "./useDraftStorage";

const DRAFT_GONE_TOAST_ID = "taskCreation.error.draftGone";

/**
 * Sends the visitor back to the description screen when a screen's PUT
 * 404s — the draft it was saving to no longer exists server-side. Shared by
 * every screen that calls `useSubmitScreenAnswers`, so the redirect is
 * written once rather than copied into each screen's own catch block
 * (RT-4321): the subcategory screen's `handleContinue` used to fall through
 * to a generic error toast on this exact response, leaving the visitor
 * stuck on a dead draft.
 *
 * Returns `true` when it handled the error — the caller should stop there —
 * and `false` for anything else, so the caller's own catch can still show
 * its generic "could not save" toast.
 */
export const useDraftGoneRedirect = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMessage } = useI18n();
  const categoryParams = useFlowCategoryParams();
  // Only a SEM landing sends this; the category picker sends `p_category`.
  const sCategory = useSearchParams("s_category");

  return (error: unknown): boolean => {
    if (!api.isApiError(error) || error.response?.status !== 404) return false;

    clearDraftStorage();
    navigate(serviceRequestPathFragment.SERVICE_REQUEST_CREATION, {
      withLocale: true,
      queryParams: {
        ...categoryParams,
        ...(sCategory ? { s_category: sCategory } : {}),
      },
    });
    if (!toast.isActive(DRAFT_GONE_TOAST_ID))
      toast({
        id: DRAFT_GONE_TOAST_ID,
        status: "error",
        description: formatMessage(messages["taskCreation.error.draftGone"]),
      });

    return true;
  };
};
